import { useState } from "react";
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/ui/item";
import { rotuloDaFuncao } from "../nomes.js";
import { useAcoes, useEstado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { Botao, Cortina, Rosto, Vazio } from "../componentes/comuns.jsx";
import { confirmar } from "@/componentes/pergunta.tsx";

/** Passar o atendimento para outra pessoa — com o histórico junto. */
export default function ModalTransferir() {
  const { equipe, mesa, caso } = useEstado();
  const { definirResponsaveis } = useAcoes();
  const { ajustar, irParaVista } = useNavegacao();
  const fechar = () => ajustar({ transferir: null });

  /*
   * A escolha é um CONJUNTO, e nasce com quem já cuida do caso.
   *
   * O atendimento se transfere para uma pessoa ou para várias, e todas ficam
   * titulares: veem em "Meus", recebem as pendências no sino, contam o caso na
   * carga e escrevem para o cliente. Nascer com a seleção atual marcada é o que
   * faz "acrescentar o Marcos" ser um toque em vez de remontar a lista — e o que
   * mostra, sem uma palavra, que confirmar SUBSTITUI o conjunto.
   *
   * A ordem importa e é a de clique: o PRIMEIRO da lista é quem a régua cobra e
   * o fluxo escala (o domínio explica em `responsaveis.definir`). Por isso é
   * array e não Set.
   */
  const jaCuidam = [
    ...(caso?.registro?.dados?.colaborador ? [String(caso.registro.dados.colaborador).toUpperCase()] : []),
    ...(caso?.acompanhantes ?? []).map((a) => String(a.pessoa).toUpperCase()),
  ];
  const [escolhidos, setEscolhidos] = useState(jaCuidam);
  const alternar = (nome) =>
    setEscolhidos((antes) => (antes.includes(nome) ? antes.filter((n) => n !== nome) : [...antes, nome]));
  const mudou = escolhidos.length !== jaCuidam.length || escolhidos.some((n, i) => n !== jaCuidam[i]);

  /*
   * Quem está mais livre primeiro.
   *
   * A ordem era a do cadastro, que não quer dizer nada para quem transfere: a
   * carga já estava escrita em cada linha e a pessoa é que fazia a conta, nome
   * por nome, para achar quem aguenta mais um. Ordenar É a recomendação — o
   * padrão que se lê como conselho, e não como trava, porque a lista inteira
   * continua ali. Empate desempata pelo nome, para a ordem não dançar a cada
   * mensagem que chega.
   */
  const porCarga = equipe
    .map((pessoa) => ({
      ...pessoa,
      carga: mesa.filter((c) => (c.responsavel ?? "").toUpperCase() === pessoa.nome.toUpperCase()).length,
    }))
    .toSorted((a, b) => a.carga - b.carga || a.nome.localeCompare(b.nome, "pt-BR"));

  return (
    <Cortina
      aoFechar={fechar}
      largura="440px"
      z={44}
      titulo="Quem cuida deste atendimento"
      acima="Marque uma pessoa ou várias. Todas ficam titulares: veem em “Meus”, recebem as pendências e escrevem para o cliente. Histórico, documentos e rascunho vão junto."
      /* guarda-ok: não há o que perder — a escolha é marcação, e confirmar
         pergunta (`confirmar` abaixo). Fechar não deixa nada digitado. */
      rodape={
        <>
          <Botao rotulo="Cancelar" aoTocar={fechar} classe="border-transparent bg-transparent text-corpo text-muted-foreground" />
          <Botao
            rotulo={
              escolhidos.length > 1
                ? `Passar para ${escolhidos.length} pessoas`
                : escolhidos.length
                  ? "Transferir"
                  : "Escolha quem cuida"
            }
            destaque
            desabilitado={!escolhidos.length || !mudou}
            titulo={
              !escolhidos.length
                ? "Marque ao menos uma pessoa — “sem ninguém” é a fila, e ela se escolhe na ficha"
                : !mudou
                  ? "É quem já cuida do caso"
                  : `${escolhidos[0]} é quem a régua vai cobrar (o primeiro da lista)`
            }
            aoTocar={async () => {
              const nomes = escolhidos.join(", ");
              if (
                await confirmar({
                  titulo:
                    escolhidos.length > 1
                      ? `Passar o atendimento para ${escolhidos.length} pessoas?`
                      : `Transferir para ${escolhidos[0]}?`,
                  texto:
                    escolhidos.length > 1
                      ? `${nomes} passam a cuidar deste caso — todas veem em “Meus”, recebem as pendências e escrevem para o cliente. A régua cobra ${escolhidos[0]}, que é o primeiro da lista. Quem cuidava antes e não está marcado sai.`
                      : `${escolhidos[0]} fica com o atendimento. Histórico, documentos e rascunho da petição vão junto — quem cuidava antes sai.`,
                  acao: escolhidos.length > 1 ? "Passar para todas" : "Transferir",
                })
              )
                definirResponsaveis(escolhidos);
            }}
          />
        </>
      }
    >
      {/* Sem ramo vazio, o escritório novo abria uma caixa que prometia levar
          histórico e rascunho e não oferecia ninguém: nem um nome, nem um
          motivo, nem o caminho. Quem fecha não sabe se é bug ou falta cadastro. */}
      {!equipe.length ? (
        <Vazio titulo="Ninguém cadastrado ainda">
          Transferir precisa de alguém na equipe. Cadastre quem atende em Equipe e acessos.
          <div className="mt-3.5">
            <Botao
              rotulo="Abrir Equipe e acessos"
              destaque
              aoTocar={() => {
                fechar();
                irParaVista("team");
              }}
            />
          </div>
        </Vazio>
      ) : null}

      {porCarga.map((pessoa, i) => {
        const carga = pessoa.carga;
        const nome = pessoa.nome.toUpperCase();
        const marcado = escolhidos.includes(nome);
        /* A ordem do clique é a ordem do conjunto, e o primeiro tem consequência
           (é quem a régua cobra). O número na marca é o que diz isso sem uma
           frase — "1" ao lado do nome e o resto em sequência. */
        const posicao = escolhidos.indexOf(nome) + 1;
        /* O primeiro da lista é a sugestão da casa — ver o resto continua a um
           olhar de distância, mas quem só quer passar o caso adiante já tem uma
           resposta pronta em vez de uma lista para julgar. */
        const sugerido = i === 0 && porCarga.length > 1 && carga < porCarga[1].carga;
        return (
          /* `Item` do shadcn como linha da lista: foto, nome, função e carga são
             mídia, título, descrição e ação — os mesmos slots em toda lista de
             pessoa da mesa. Ele nasce `div`, e aqui a linha inteira É o botão
             (`render`), então o alvo de clique continua sendo a linha toda. */
          <Item
            key={pessoa.id}
            variant="outline"
            size="sm"
            render={<button type="button" />}
            /* O clique MARCA, e quem grava é o botão do rodapé.
               Antes um clique aqui entregava o caso inteiro e escrevia na trilha:
               a caixa mostrava a carga de cada um justamente para a pessoa PENSAR
               antes, e o gesto não dava tempo de pensar. Com conjunto, marcar e
               confirmar são dois gestos por necessidade — e a pergunta do rodapé
               diz quem entra, quem sai e quem a régua vai cobrar. */
            aria-pressed={marcado}
            onClick={() => alternar(nome)}
            className={`contorno mb-2 gap-3 rounded-[14px] px-3 py-[12px] text-left ${
              marcado ? "border-verde-fundo/45 bg-verde-fundo/8" : "border-border bg-secondary"
            }`}
          >
            {/* A marca com a POSIÇÃO: quadrado vazio quando fora, número quando
                dentro — e o "1" é o que diz qual deles a régua cobra. */}
            <span
              className={`flex size-[24px] flex-none items-center justify-center rounded-lg border-2 font-mono text-apoio font-bold ${
                marcado ? "border-verde-fundo bg-verde-fundo text-creme" : "border-foreground/20 bg-background text-transparent"
              }`}
              aria-hidden="true"
            >
              {marcado ? posicao : ""}
            </span>
            <ItemMedia>
              <Rosto pessoa={pessoa} tamanho={40} raio={12} />
            </ItemMedia>
            <ItemContent className="gap-0">
              <ItemTitle className="text-corpo font-semibold">{pessoa.nome}</ItemTitle>
              <ItemDescription className="overflow-hidden text-selo whitespace-nowrap text-ellipsis text-muted-foreground">
                {rotuloDaFuncao(pessoa.funcao)}
              </ItemDescription>
            </ItemContent>
            <span className="flex-none text-right">
              {sugerido ? <span className="mr-2 text-selo font-semibold text-verde">mais livre</span> : null}
              <span className={`font-mono text-selo ${carga > 8 ? "text-primary" : "text-verde"}`}>{carga} ativos</span>
            </span>
          </Item>
        );
      })}
    </Cortina>
  );
}
